/**
 * /admin/users/:id/activity — per-user activity dashboard.
 *
 * Single round-trip to `/api/v1/auth/users/:id/activity` returns the user
 * summary + content counts + recent audit events; the page just renders
 * what comes back. Admin-only on both sides: backend gates on the
 * `audit:read` permission, frontend wraps everything in `<RoleGate>`.
 *
 * Per the U1/U2 scoping conversation: this is product-usage analytics,
 * not a compliance audit trail (that's the /admin > Audit tab from U1).
 * The deputy information officer signed off on the POPIA posture for
 * admin visibility into per-user activity, in line with internal
 * policies around system usage tracking data.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Activity, QrCode, Link2, FileText, Users as UsersIcon, Inbox,
  Mail, Shield, ShieldCheck, Calendar, Clock,
} from 'lucide-react';

import { apiV1 } from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate, NotAuthorized } from '@/components/auth';

// Matches the dictionary in Admin.jsx — kept in sync so the human-friendly
// labels feel the same everywhere audit events surface.
const AUDIT_ACTION_LABELS = {
  'auth.alchemy.exchange.success': 'Signed in (SSO)',
  'auth.alchemy.exchange.failed':  'SSO sign-in failed',
  'auth.alchemy.check.failed':     'Session expired',
  'auth.alchemy.logout':           'Signed out',
  'auth.alchemy.user.provisioned': 'Provisioned via SSO',
  'auth.local.login.success':      'Signed in (admin)',
  'auth.local.login.failed':       'Admin sign-in failed',
  'auth.local.login.rejected_sso_user': 'Blocked — SSO user',
  'auth.onboarding.completed':     'Completed onboarding',
  'qr.created':       'Created QR code',
  'qr.deleted':       'Deleted QR code',
  'url.created':      'Created short URL',
  'url.deleted':      'Deleted short URL',
  'page.created':     'Created landing page',
  'page.published':   'Published landing page',
  'page.unpublished': 'Unpublished landing page',
  'page.deleted':     'Deleted landing page',
  'bio.created':      'Created bio page',
  'bio.deleted':      'Deleted bio page',
  'lead.created':     'Created lead',
  'lead.deleted':     'Deleted lead',
  'user.created':         'Created user',
  'user.role_changed':    'Changed user role',
  'user.status_changed':  'Changed user status',
  'user.deleted':         'Deleted user',
  'api_key.created':      'Created API key',
  'api_key.revoked':      'Revoked API key',
  'user.permission_granted': 'Granted a permission',
  'user.permission_revoked': 'Revoked a permission',
  'audience.list.viewed':       'Viewed audience extracts',
  'audience.areas.viewed':      'Viewed high-value areas',
  'audience.file.downloaded':   'Downloaded an audience file',
  'audience.rebuild.requested': 'Requested an audience rebuild',
};

const CONTENT_CATEGORIES = [
  { key: 'qr',   icon: QrCode,    label: 'QR codes' },
  { key: 'url',  icon: Link2,     label: 'Short URLs' },
  { key: 'page', icon: FileText,  label: 'Landing pages' },
  { key: 'bio',  icon: UsersIcon, label: 'Bio pages' },
  { key: 'lead', icon: Inbox,     label: 'Leads' },
];

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().replace('T', ' ').replace(/\..+$/, '') + ' UTC';
  } catch {
    return String(iso);
  }
}

function formatRelative(iso) {
  if (!iso) return 'Never';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000)        return 'Just now';
    if (ms < 3_600_000)     return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000)    return `${Math.floor(ms / 3_600_000)}h ago`;
    if (ms < 30 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return 'Unknown';
  }
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-start gap-3">
        <div className="rounded-md bg-muted p-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-1 truncate">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function UserActivityInner() {
  const { id: userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiV1.get(`/auth/users/${userId}/activity`);
        if (cancelled) return;
        if (resp?.data?.success) {
          setData(resp.data.data || null);
        } else {
          setError(resp?.data?.detail || 'Failed to load activity');
        }
      } catch (err) {
        if (!cancelled) {
          // 404 = unknown user; 403 = lost admin role mid-session;
          // anything else is a generic error.
          const status = err?.status || err?.response?.status;
          if (status === 404) setError('User not found.');
          else if (status === 403) setError('You no longer have access to this page.');
          else setError(err?.detail || err?.message || 'Failed to load activity');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId]);

  // Sum of all created content — quick "are they using LinkHub at all?" signal.
  const totalCreated = useMemo(() => {
    if (!data?.content_counts) return 0;
    return CONTENT_CATEGORIES.reduce(
      (acc, c) => acc + (data.content_counts[c.key]?.total || 0),
      0,
    );
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4 max-w-2xl" data-testid="user-activity-error">
        <Link to="/users" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to user management
        </Link>
        <Card>
          <CardContent className="p-6 text-center space-y-2">
            <p className="font-medium">{error}</p>
            <p className="text-sm text-muted-foreground">
              Confirm the user ID is correct, or return to the user list.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { user, content_counts: counts = {}, recent_activity: events = [], total_events: totalEvents = 0 } = data;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="user-activity-page">
      {/* Back nav */}
      <Link to="/users" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to user management
      </Link>

      {/* User header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            {user.full_name || user.email}
          </h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            <span data-testid="user-activity-email">{user.email}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant="outline" className="capitalize">
            {user.role}
          </Badge>
          <Badge variant={user.status === 'active' ? 'default' : 'secondary'} className="capitalize">
            {user.status}
          </Badge>
          <Badge variant="outline" className="capitalize" data-testid="user-activity-auth-method">
            {user.auth_method === 'alchemy' ? <><ShieldCheck className="h-3 w-3 mr-1" /> SSO</> : <><Shield className="h-3 w-3 mr-1" /> Local</>}
          </Badge>
        </div>
      </div>

      {/* At-a-glance stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Calendar}
          label="Last login"
          value={formatRelative(user.last_login_at)}
          hint={user.last_login_at ? formatTimestamp(user.last_login_at) : 'No recorded sign-in'}
        />
        <StatCard
          icon={Activity}
          label="Total sign-ins"
          value={user.login_count}
          hint={user.onboarded_at ? `Onboarded ${formatRelative(user.onboarded_at)}` : 'Not yet onboarded'}
        />
        <StatCard
          icon={QrCode}
          label="Items created"
          value={totalCreated}
          hint="QRs, URLs, pages, bios, leads — all time"
        />
        <StatCard
          icon={Clock}
          label="Audit events"
          value={totalEvents}
          hint={totalEvents > events.length ? `Showing latest ${events.length}` : 'All on this page'}
        />
      </div>

      {/* Content breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What they've created</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="user-activity-content-counts">
            {CONTENT_CATEGORIES.map(({ key, icon: Icon, label }) => {
              const c = counts[key] || { total: 0, last_7d: 0, last_30d: 0 };
              return (
                <div key={key} className="rounded-md border p-3 space-y-1.5" data-testid={`content-count-${key}`}>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" />
                    <span className="text-xs uppercase tracking-wider">{label}</span>
                  </div>
                  <p className="text-2xl font-semibold">{c.total}</p>
                  <div className="text-xs text-muted-foreground flex gap-3">
                    <span><strong>{c.last_7d}</strong> in 7d</span>
                    <span><strong>{c.last_30d}</strong> in 30d</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent activity</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="border rounded-lg" data-testid="user-activity-event-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Timestamp (UTC)</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead className="w-[140px]">From</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No audit events yet for this user.
                    </TableCell>
                  </TableRow>
                )}
                {events.map((event, idx) => {
                  const meta = event.meta || {};
                  const friendly = meta.title || meta.slug || meta.short_code || meta.name;
                  const resource = !event.target
                    ? ''
                    : friendly ? `${friendly} (${event.target})` : event.target;
                  return (
                    <TableRow key={`${event.createdAt}-${idx}`} data-testid={`activity-row-${event.action}`}>
                      <TableCell className="text-muted-foreground font-mono text-xs whitespace-nowrap">
                        {formatTimestamp(event.createdAt)}
                      </TableCell>
                      <TableCell>{AUDIT_ACTION_LABELS[event.action] || event.action}</TableCell>
                      <TableCell className="text-muted-foreground">{resource}</TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">{event.actorIp || ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function UserActivity() {
  return (
    <RoleGate
      role="admin"
      fallback={
        <NotAuthorized
          title="User activity is admin-only"
          description="This page is restricted to administrators because it surfaces detailed per-user behaviour."
        />
      }
    >
      <UserActivityInner />
    </RoleGate>
  );
}
